import React, { useState, useEffect, useCallback } from 'react';
import { Settings, Save, Lock, Shield, Bell, Palette, Globe, Database, Key, Eye, EyeOff, AlertTriangle, CheckCircle, Plug, Github, Mail, MessageSquare, FileText, Table, Users, Calendar, Layers, Loader, Check } from 'lucide-react';
import { useNotifications } from './NotificationSystem';
import apiService from '../services/api';
import composioService from '../services/composio';

const SettingsPanel = ({ onClose }) => {
  const { showSuccess, showError } = useNotifications();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // General Settings
  const [settings, setSettings] = useState({
    theme: 'light',
    language: 'en',
    autoSave: true,
    compactMode: false,
    showTooltips: true,
    defaultModel: 'gpt-4o-mini',
    defaultTemperature: 0.7,
  });

  // Security Settings
  const [security, setSecurity] = useState({
    enable2FA: false,
    sessionTimeout: 30,
    requirePasswordChange: false,
    apiKeyRotation: 90,
    enableAuditLog: true,
  });

  // API Keys (masked)
  const [apiKeys, setApiKeys] = useState({
    openai: '',
    anthropic: '',
    custom: '',
  });
  const [showKeys, setShowKeys] = useState({});

  // Notifications
  const [notifications, setNotifications] = useState({
    emailOnError: true,
    emailOnCompletion: false,
    slackWebhook: '',
    discordWebhook: '',
  });

  // Load settings from backend/localStorage
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const savedSettings = localStorage.getItem('ai-flow-settings');
        if (savedSettings) {
          const parsed = JSON.parse(savedSettings);
          setSettings(prev => ({ ...prev, ...parsed.settings }));
          setSecurity(prev => ({ ...prev, ...parsed.security }));
          setNotifications(prev => ({ ...prev, ...parsed.notifications }));
          
          // Load API keys (masked)
          if (parsed.apiKeys) {
            setApiKeys(prev => ({
              ...prev,
              openai: parsed.apiKeys.openai ? maskKey(parsed.apiKeys.openai) : '',
              anthropic: parsed.apiKeys.anthropic ? maskKey(parsed.apiKeys.anthropic) : '',
              custom: parsed.apiKeys.custom ? maskKey(parsed.apiKeys.custom) : '',
            }));
          }
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    loadSettings();
  }, []);

  const maskKey = (key) => {
    if (!key || key.length < 8) return '••••••••';
    return key.substring(0, 4) + '••••••••' + key.substring(key.length - 4);
  };

  const validateApiKey = (key) => {
    if (!key || key.includes('••••')) return true; // Already masked, assume valid
    return key.length >= 20 && /^[a-zA-Z0-9_-]+$/.test(key);
  };

  const validateWebhook = (url) => {
    if (!url) return true;
    try {
      const parsed = new URL(url);
      return ['http:', 'https:'].includes(parsed.protocol) && 
             !['localhost', '127.0.0.1', '0.0.0.0'].includes(parsed.hostname);
    } catch {
      return false;
    }
  };

  const handleSave = async () => {
    setLoading(true);
    setSaved(false);
    
    try {
      // Validate API keys
      if (apiKeys.openai && !validateApiKey(apiKeys.openai)) {
        showError('Invalid OpenAI API key format. Keys should be at least 20 characters and contain only alphanumeric characters, hyphens, and underscores.', 'Validation Error');
        setLoading(false);
        return;
      }
      if (apiKeys.anthropic && !validateApiKey(apiKeys.anthropic)) {
        showError('Invalid Anthropic API key format. Keys should be at least 20 characters and contain only alphanumeric characters, hyphens, and underscores.', 'Validation Error');
        setLoading(false);
        return;
      }

      // Validate webhooks
      if (notifications.slackWebhook && !validateWebhook(notifications.slackWebhook)) {
        showError('Invalid Slack webhook URL. Must be a valid HTTPS URL and cannot point to localhost or private IPs.', 'Validation Error');
        setLoading(false);
        return;
      }
      if (notifications.discordWebhook && !validateWebhook(notifications.discordWebhook)) {
        showError('Invalid Discord webhook URL. Must be a valid HTTPS URL and cannot point to localhost or private IPs.', 'Validation Error');
        setLoading(false);
        return;
      }

      // Save to backend API (with security)
      // Get current saved keys to preserve masked ones
      let savedKeys = {};
      try {
        const saved = localStorage.getItem('ai-flow-settings');
        if (saved) {
          const parsed = JSON.parse(saved);
          savedKeys = parsed.apiKeys || {};
        }
      } catch (e) {
        // Ignore parse errors
      }
      
      // Helper function to determine if we should save a key
      const shouldSaveKey = (currentKey, savedKey) => {
        // If current key is masked (contains ••••), keep the saved one
        if (currentKey && currentKey.includes('••••')) {
          return savedKey || undefined;
        }
        // If current key exists and is not masked, save it
        if (currentKey && currentKey.trim().length > 0) {
          return currentKey;
        }
        // Otherwise, keep saved key if it exists
        return savedKey || undefined;
      };
      
      const settingsData = {
        settings,
        security,
        notifications,
        apiKeys: {
          openai: shouldSaveKey(apiKeys.openai, savedKeys.openai),
          anthropic: shouldSaveKey(apiKeys.anthropic, savedKeys.anthropic),
          custom: shouldSaveKey(apiKeys.custom, savedKeys.custom),
        },
      };

      // Try backend first
      try {
        const base = (window.API_BASE_URL || '/api').replace(/\/$/, '');
        const response = await fetch(`${base}/settings`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(settingsData),
          credentials: 'same-origin',
        });

        if (!response.ok) {
          throw new Error('Backend save failed');
        }
      } catch (backendError) {
        console.warn('Backend save failed, using localStorage:', backendError);
        // Fallback to localStorage
        localStorage.setItem('ai-flow-settings', JSON.stringify(settingsData));
      }

      // Apply settings immediately
      if (settings.theme) {
        document.documentElement.setAttribute('data-theme', settings.theme);
      }

      setSaved(true);
      showSuccess('Settings saved successfully!', 'Settings Saved');
      setTimeout(() => setSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save settings:', error);
      showError('Failed to save settings. Please try again.', 'Save Error');
    } finally {
      setLoading(false);
    }
  };

  const handleApiKeyChange = (provider, value) => {
    setApiKeys(prev => ({
      ...prev,
      [provider]: value,
    }));
  };

  const toggleKeyVisibility = (provider) => {
    setShowKeys(prev => ({
      ...prev,
      [provider]: !prev[provider],
    }));
  };

  // Integrations state
  const [connections, setConnections] = useState({});
  const [intLoading, setIntLoading] = useState(false);
  const [connecting, setConnecting] = useState(null);
  const [intError, setIntError] = useState(null);

  useEffect(() => {
    if (activeTab === 'integrations') loadConnections();
  }, [activeTab]);

  async function loadConnections() {
    setIntLoading(true);
    const res = await composioService.getConnections();
    const map = {};
    for (const c of (res.connections || [])) map[c.toolkit] = c;
    setConnections(map);
    setIntLoading(false);
  }

  async function handleConnect(toolkit) {
    setConnecting(toolkit); setIntError(null);
    try {
      await composioService.connectToolkit(toolkit);
      await loadConnections();
    } catch (e) {
      setIntError(e.message);
    } finally {
      setConnecting(null);
    }
  }

  const TOOLKITS = [
    { id: 'gmail',         label: 'Gmail',     icon: Mail,          color: '#ea4335' },
    { id: 'slack',         label: 'Slack',     icon: MessageSquare, color: '#4a154b' },
    { id: 'github',        label: 'GitHub',    icon: Github,        color: '#24292e' },
    { id: 'notion',        label: 'Notion',    icon: FileText,      color: '#6366f1' },
    { id: 'linear',        label: 'Linear',    icon: Layers,        color: '#5e6ad2' },
    { id: 'googlecalendar',label: 'Calendar',  icon: Calendar,      color: '#4285f4' },
    { id: 'googledrive',   label: 'Drive',     icon: Globe,         color: '#0f9d58' },
    { id: 'discord',       label: 'Discord',   icon: MessageSquare, color: '#5865f2' },
    { id: 'airtable',      label: 'Airtable',  icon: Table,         color: '#18bfff' },
    { id: 'hubspot',       label: 'HubSpot',   icon: Users,         color: '#ff7a59' },
    { id: 'jira',          label: 'Jira',      icon: Globe,         color: '#0052cc' },
    { id: 'twitter',       label: 'Twitter/X', icon: Globe,         color: '#000000' },
  ];

  const tabs = [
    { id: 'general',      label: 'General',      icon: Settings },
    { id: 'security',     label: 'Security',     icon: Shield },
    { id: 'api',          label: 'API Keys',     icon: Key },
    { id: 'notifications',label: 'Notifications',icon: Bell },
    { id: 'integrations', label: 'Integrations', icon: Plug },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Settings size={20} className="text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-800">Settings</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close settings"
          >
            ×
          </button>
        </div>

        <div className="flex h-[calc(90vh-60px)]">
          {/* Sidebar */}
          <div className="w-48 border-r border-gray-200 bg-gray-50">
            <div className="p-2 space-y-1">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'general' && (
              <div className="space-y-6">
                <h4 className="text-lg font-semibold text-gray-800">General Settings</h4>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
                  <select
                    value={settings.theme}
                    onChange={(e) => setSettings(prev => ({ ...prev, theme: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                    <option value="auto">Auto</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
                  <select
                    value={settings.language}
                    onChange={(e) => setSettings(prev => ({ ...prev, language: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="en">English</option>
                    <option value="es">Spanish</option>
                    <option value="fr">French</option>
                    <option value="de">German</option>
                  </select>
                </div>

                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.autoSave}
                      onChange={(e) => setSettings(prev => ({ ...prev, autoSave: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Auto-save flows</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.compactMode}
                      onChange={(e) => setSettings(prev => ({ ...prev, compactMode: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Compact mode (70%)</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings.showTooltips}
                      onChange={(e) => setSettings(prev => ({ ...prev, showTooltips: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Show tooltips</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Default AI Model</label>
                  <select
                    value={settings.defaultModel}
                    onChange={(e) => setSettings(prev => ({ ...prev, defaultModel: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="gpt-4o-mini">GPT-4o Mini</option>
                    <option value="gpt-4o">GPT-4o</option>
                    <option value="gpt-4-turbo">GPT-4 Turbo</option>
                    <option value="claude-3-5-sonnet-20240620">Claude 3.5 Sonnet</option>
                    <option value="claude-3-opus-20240229">Claude 3 Opus</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Default Temperature: {settings.defaultTemperature}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={settings.defaultTemperature}
                    onChange={(e) => setSettings(prev => ({ ...prev, defaultTemperature: parseFloat(e.target.value) }))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>Focused (0.0)</span>
                    <span>Balanced (0.5)</span>
                    <span>Creative (1.0)</span>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'security' && (
              <div className="space-y-6">
                <h4 className="text-lg font-semibold text-gray-800">Security Settings</h4>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={20} className="text-yellow-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Security Best Practices</p>
                      <p className="text-xs text-yellow-700 mt-1">
                        Enable 2FA and regular password changes for enhanced security. Audit logs help track all system access.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={security.enable2FA}
                      onChange={(e) => setSecurity(prev => ({ ...prev, enable2FA: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Enable Two-Factor Authentication (2FA)</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={security.enableAuditLog}
                      onChange={(e) => setSecurity(prev => ({ ...prev, enableAuditLog: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Enable Audit Logging</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={security.requirePasswordChange}
                      onChange={(e) => setSecurity(prev => ({ ...prev, requirePasswordChange: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Require Password Change Every 90 Days</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Session Timeout (minutes)
                  </label>
                  <input
                    type="number"
                    min="5"
                    max="480"
                    value={security.sessionTimeout}
                    onChange={(e) => setSecurity(prev => ({ ...prev, sessionTimeout: parseInt(e.target.value) || 30 }))}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    API Key Rotation Period (days)
                  </label>
                  <input
                    type="number"
                    min="30"
                    max="365"
                    value={security.apiKeyRotation}
                    onChange={(e) => setSecurity(prev => ({ ...prev, apiKeyRotation: parseInt(e.target.value) || 90 }))}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            {activeTab === 'api' && (
              <div className="space-y-6">
                <h4 className="text-lg font-semibold text-gray-800">API Keys</h4>
                
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <Lock size={20} className="text-red-600 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800">Secure API Key Storage</p>
                      <p className="text-xs text-red-700 mt-1">
                        API keys are encrypted and stored securely. Never share your keys with anyone.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    OpenAI API Key
                  </label>
                  <div className="flex gap-2">
                    <input
                      type={showKeys.openai ? 'text' : 'password'}
                      value={apiKeys.openai}
                      onChange={(e) => handleApiKeyChange('openai', e.target.value)}
                      placeholder="sk-..."
                      className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    />
                    <button
                      onClick={() => toggleKeyVisibility('openai')}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      title={showKeys.openai ? 'Hide key' : 'Show key'}
                    >
                      {showKeys.openai ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Get your API key from <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">OpenAI Platform</a>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Anthropic API Key
                  </label>
                  <div className="flex gap-2">
                    <input
                      type={showKeys.anthropic ? 'text' : 'password'}
                      value={apiKeys.anthropic}
                      onChange={(e) => handleApiKeyChange('anthropic', e.target.value)}
                      placeholder="sk-ant-..."
                      className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    />
                    <button
                      onClick={() => toggleKeyVisibility('anthropic')}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      title={showKeys.anthropic ? 'Hide key' : 'Show key'}
                    >
                      {showKeys.anthropic ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Get your API key from <a href="https://console.anthropic.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Anthropic Console</a>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom API Key (Optional)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type={showKeys.custom ? 'text' : 'password'}
                      value={apiKeys.custom}
                      onChange={(e) => handleApiKeyChange('custom', e.target.value)}
                      placeholder="Enter custom API key..."
                      className="flex-1 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    />
                    <button
                      onClick={() => toggleKeyVisibility('custom')}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                      title={showKeys.custom ? 'Hide key' : 'Show key'}
                    >
                      {showKeys.custom ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'integrations' && (
              <div className="space-y-4">
                <div>
                  <h4 className="text-lg font-semibold text-gray-800">App Connections</h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Connect apps so your AI agents can take action — send emails, create tasks, update Notion, and more. Connections use OAuth, no passwords stored.
                  </p>
                </div>

                {intError && (
                  <div className="flex items-start gap-2 rounded-lg px-3 py-2 text-sm bg-red-50 text-red-700 border border-red-200">
                    <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                    <span>{intError}</span>
                  </div>
                )}

                {intLoading ? (
                  <div className="flex justify-center py-10">
                    <Loader size={22} className="animate-spin text-blue-500" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {TOOLKITS.map(tk => {
                      const connected = !!connections[tk.id];
                      const isConnecting = connecting === tk.id;
                      const Icon = tk.icon;
                      return (
                        <div
                          key={tk.id}
                          className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border transition-colors ${
                            connected ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center justify-center w-7 h-7 rounded-lg shrink-0" style={{ background: tk.color + '18' }}>
                            <Icon size={14} style={{ color: tk.color }} />
                          </div>
                          <span className="flex-1 text-sm font-medium text-gray-800">{tk.label}</span>
                          {connected ? (
                            <span className="flex items-center gap-1 text-xs font-medium text-green-600">
                              <Check size={12} /> Connected
                            </span>
                          ) : (
                            <button
                              onClick={() => handleConnect(tk.id)}
                              disabled={!!connecting}
                              className="text-xs font-medium px-2.5 py-1 rounded-lg border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                            >
                              {isConnecting && <Loader size={11} className="animate-spin" />}
                              {isConnecting ? 'Connecting…' : 'Connect'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h4 className="text-lg font-semibold text-gray-800">Notifications</h4>
                
                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifications.emailOnError}
                      onChange={(e) => setNotifications(prev => ({ ...prev, emailOnError: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Email on flow execution errors</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifications.emailOnCompletion}
                      onChange={(e) => setNotifications(prev => ({ ...prev, emailOnCompletion: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Email on flow completion</span>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Slack Webhook URL
                  </label>
                  <input
                    type="url"
                    value={notifications.slackWebhook}
                    onChange={(e) => setNotifications(prev => ({ ...prev, slackWebhook: e.target.value }))}
                    placeholder="https://hooks.slack.com/services/..."
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Receive notifications in your Slack channel
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Discord Webhook URL
                  </label>
                  <input
                    type="url"
                    value={notifications.discordWebhook}
                    onChange={(e) => setNotifications(prev => ({ ...prev, discordWebhook: e.target.value }))}
                    placeholder="https://discord.com/api/webhooks/..."
                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Receive notifications in your Discord channel
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            {saved && (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle size={16} />
                <span className="text-sm">Settings saved successfully</span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              <Save size={16} />
              {loading ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;

